import React from "react";
import { ArrowLeft, Trash2, Shield, AlertTriangle, CheckCircle, UserX } from "lucide-react";
import { Link } from "react-router-dom";

const DeleteAccountPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="bg-panel py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center group">
              <ArrowLeft className="h-5 w-5 text-panel-muted mr-2 group-hover:text-panel-ink transition-colors" />
              <span className="text-panel-muted group-hover:text-panel-ink transition-colors">
                Return to DressUp Home
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-surface shadow-soft rounded-none border border-line p-8 mb-12 transition-all duration-300 hover:shadow-lg text-ink">
          <div className="flex items-center mb-8">
            <Trash2 className="h-8 w-8 text-danger mr-3" />
            <h1 className="text-2xl md:text-3xl font-bold text-ink">
              Delete Your DressUp Account
            </h1>
          </div>

          <div className="prose prose-brand max-w-none">
            <div className="bg-surface rounded-none p-4 mb-8 border-l-2 border-danger">
              <p className="text-danger font-medium">Last updated: April 24, 2025</p>
            </div>

            {/* Important Notice */}
            <div className="bg-surface rounded-none p-6 mb-8 border-l-2 border-danger">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-danger mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-danger mb-2">Important Notice</h3>
                  <p className="text-muted">
                    This page explains how to request the deletion of your DressUp account and associated data. 
                    Account deletion is permanent and cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            {/* How to Delete Account */}
            <h2 className="flex items-center text-ink text-xl md:text-2xl mb-4">
              <UserX className="h-5 w-5 mr-2 text-danger" />
              How to Delete Your Account
            </h2>
            
            <div className="bg-surface rounded-none p-6 mb-8">
              <h3 className="text-lg font-semibold text-ink mb-4">Step-by-Step Process:</h3>
              <ol className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-ink text-paper rounded-none w-6 h-6 flex items-center justify-center text-sm font-medium mr-3 mt-0.5 flex-shrink-0">1</span>
                  <span>Open the DressUp app on your device</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-ink text-paper rounded-none w-6 h-6 flex items-center justify-center text-sm font-medium mr-3 mt-0.5 flex-shrink-0">2</span>
                  <span>Navigate to your Profile page</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-ink text-paper rounded-none w-6 h-6 flex items-center justify-center text-sm font-medium mr-3 mt-0.5 flex-shrink-0">3</span>
                  <span>Tap on "Delete Account" option</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-ink text-paper rounded-none w-6 h-6 flex items-center justify-center text-sm font-medium mr-3 mt-0.5 flex-shrink-0">4</span>
                  <span>Confirm your decision by entering your password</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-ink text-paper rounded-none w-6 h-6 flex items-center justify-center text-sm font-medium mr-3 mt-0.5 flex-shrink-0">5</span>
                  <span>Your account and all associated data will be permanently deleted</span>
                </li>
              </ol>
            </div>

            {/* Data Deletion Information */}
            <h2 className="flex items-center text-ink text-xl md:text-2xl mb-4">
              <Shield className="h-5 w-5 mr-2 text-danger" />
              What Data Gets Deleted
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-surface rounded-none p-6 border border-line">
                <h3 className="text-lg font-semibold text-success mb-3 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Data That Gets Deleted
                </h3>
                <ul className="space-y-2 text-muted">
                  <li>• Your user profile and account information</li>
                  <li>• Personal preferences and style data</li>
                  <li>• Usage history and app interactions</li>
                  <li>• Stored images and fashion preferences</li>
                  <li>• Authentication credentials</li>
                  <li>• Any other personal data associated with your account</li>
                </ul>
              </div>
              
              <div className="bg-surface rounded-none p-6 border border-line">
                <h3 className="text-lg font-semibold text-ink mb-3 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  Data Retention Policy
                </h3>
                <div className="text-muted">
                  <p className="mb-3"><strong>No Retention Period:</strong></p>
                  <p>DressUp does not retain any of your personal data after account deletion. All data is permanently removed from our systems immediately upon account deletion.</p>
                </div>
              </div>
            </div>

            {/* Privacy Commitment */}
            <div className="bg-surface rounded-none p-6 mb-8 border-l-4 border-accent">
              <h3 className="text-lg font-semibold text-ink mb-3">Our Privacy Commitment</h3>
              <p className="text-muted">
                At DressUp, we are committed to protecting your privacy and giving you full control over your data. 
                When you delete your account, we ensure that all your personal information is completely removed from our systems 
                without any retention period. This process is irreversible and designed to respect your right to be forgotten.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-surface rounded-none p-6 border border-line">
              <h3 className="text-lg font-semibold text-ink mb-3">Need Help?</h3>
              <p className="text-muted mb-3">
                If you have any questions about account deletion or need assistance with the process, 
                please contact our support team:
              </p>
              <div className="space-y-2">
                <p className="text-muted">
                  <strong>Email:</strong> support@dressup.ai
                </p>
                <p className="text-muted">
                  <strong>Response Time:</strong> Within 24-48 hours
                </p>
              </div>
            </div>

            {/* Legal Notice */}
            <div className="mt-8 p-4 bg-surface rounded-none border border-line">
              <p className="text-sm text-muted text-center">
                This account deletion process complies with data protection regulations and Google Play Store requirements. 
                By deleting your account, you acknowledge that this action is permanent and cannot be undone.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DeleteAccountPage; 